import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "@/hooks/useAuth";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Message, Notification } from "@shared/schema";

interface TypingStatus {
  fromUserId: string;
  isTyping: boolean;
}

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  sendMessage: (
    toUserId: string, 
    message: string, 
    messageType?: string,
    attachment?: { url: string; name: string; type: string; size: number },
    loadId?: string
  ) => Promise<Message | null>;
  sendTypingStatus: (toUserId: string, isTyping: boolean) => void;
  markAsRead: (fromUserId: string) => void;
  setViewingConversation: (partnerId: string | null) => void;
  typingUsers: Map<string, boolean>;
}

const SocketContext = createContext<SocketContextType | null>(null);

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Map<string, boolean>>(new Map());
  const typingTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    if (isLoading || !user) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const newSocket = io({
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on("connect", () => {
      console.log("[Socket.IO] Connected");
      setIsConnected(true);
    });

    newSocket.on("disconnect", () => {
      console.log("[Socket.IO] Disconnected");
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("[Socket.IO] Connection error:", error.message);
      if ((error as any).data?.impersonationReadOnly) {
        toast({ title: "Read-only view", description: error.message, variant: "destructive" });
        newSocket.disconnect();
      }
    });

    newSocket.on("impersonation_read_only", (response: any) => {
      toast({ title: "Read-only view", description: response.message, variant: "destructive" });
    });

    newSocket.on("new_message", (message: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/messages/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/thread"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/unread-count"] });
    });

    newSocket.on("message_sent", (message: Message) => {
      console.log("[Socket.IO] message_sent payload:", JSON.stringify(message));
      queryClient.invalidateQueries({ queryKey: ["/api/messages/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/thread", message.toUserId] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
    });

    newSocket.on("messages_read", (data: { byUserId: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/conversation", data.byUserId] });
    });

    newSocket.on("user_typing", (data: TypingStatus) => {
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        if (data.isTyping) {
          newMap.set(data.fromUserId, true);
          
          const existingTimeout = typingTimeoutsRef.current.get(data.fromUserId);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
          }
          
          const timeout = setTimeout(() => {
            setTypingUsers(p => {
              const updated = new Map(p);
              updated.delete(data.fromUserId);
              return updated;
            });
            typingTimeoutsRef.current.delete(data.fromUserId);
          }, 3000);
          
          typingTimeoutsRef.current.set(data.fromUserId, timeout);
        } else {
          newMap.delete(data.fromUserId);
          const existingTimeout = typingTimeoutsRef.current.get(data.fromUserId);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
            typingTimeoutsRef.current.delete(data.fromUserId);
          }
        }
        return newMap;
      });
    });

    newSocket.on("new_notification", (_notification: Notification) => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    });

    newSocket.on("shipment_completed", (data: { loadId: string; loadIdentifier: string; autoCompleted: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/messages/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/thread"] });
      queryClient.invalidateQueries({ queryKey: ["/api/loads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/loads", data.loadId] });
    });

    newSocket.on("shipment_auto_completed", (data: { loadId: string; loadIdentifier: string; autoCompleted: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/messages/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/thread"] });
      queryClient.invalidateQueries({ queryKey: ["/api/loads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/loads", data.loadId] });
      toast({
        title: "Shipment auto-completed",
        description: `Load ${data.loadIdentifier} was automatically marked as completed after no delivery response was received.`,
      });
    });

    newSocket.on("rating_submitted", (data: { threadId: string; raterId: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/shipment-ratings/thread", data.threadId] });
    });

    setSocket(newSocket);

    return () => {
      typingTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      typingTimeoutsRef.current.clear();
      newSocket.disconnect();
    };
  }, [user, isLoading]);

  const sendMessage = useCallback(async (
    toUserId: string, 
    message: string, 
    messageType: string = "text",
    attachment?: {
      url: string;
      name: string;
      type: string;
      size: number;
    },
    loadId?: string
  ): Promise<Message | null> => {
    if (!socket || !isConnected) {
      console.warn("[Socket.IO] Cannot send message - not connected");
      return null;
    }

    return new Promise<Message | null>((resolve, reject) => {
      socket.emit("send_message", { 
        toUserId, 
        message, 
        messageType: attachment ? 'attachment' : messageType,
        loadId,
        attachmentUrl: attachment?.url,
        attachmentName: attachment?.name,
        attachmentType: attachment?.type,
        attachmentSize: attachment?.size,
      }, (response: any) => {
        if (response.error) {
          console.error("[Socket.IO] Send message error:", response.error);
          if (response.status === 403 && (response.impersonationReadOnly || response.listingUnavailable || ["Cancelled", "Suspended"].includes(response.membershipTier))) {
            reject(new Error(response.message));
            return;
          }
          resolve(null);
        } else {
          resolve(response.message);
        }
      });
    });
  }, [socket, isConnected]);

  const sendTypingStatus = useCallback((toUserId: string, isTyping: boolean) => {
    if (socket && isConnected) {
      socket.emit("typing", { toUserId, isTyping });
    }
  }, [socket, isConnected]);

  const markAsRead = useCallback((fromUserId: string) => {
    if (socket && isConnected) {
      socket.emit("mark_read", { fromUserId });
    }
  }, [socket, isConnected]);

  const setViewingConversation = useCallback((partnerId: string | null) => {
    if (socket && isConnected) {
      socket.emit("viewing_conversation", { partnerId });
    }
  }, [socket, isConnected]);

  return (
    <SocketContext.Provider value={{
      socket,
      isConnected,
      sendMessage,
      sendTypingStatus,
      markAsRead,
      setViewingConversation,
      typingUsers,
    }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
}
